<script>
import { theme } from '@shared/composables/useGlobalConfig'
import { useNoScroll } from '@shared/composables/useNoScroll'
import { useConfig } from '@shared/stores/config.js'
import { generateSignatureTemplate } from '@shared/utils/signatureTemplate'
import { useHead } from '@unhead/vue'
import { formButton, formInput } from 'tb-design_system-web'
import { useForm } from 'vee-validate'
import { ref, watch, computed, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import * as yup from 'yup'

export default {
  name: 'formModalSignature',

  components: {
    formInput,
    formButton
  },

  props: {
    title: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  setup(props, { emit }) {
    useHead({ title: props.title })

    const contentActive = ref(false)
    const scroll = ref(false)
    const { setNoScroll } = useNoScroll()

    watch(scroll, (newValue) => {
      setNoScroll(newValue)
    })

    const { handleSubmit } = useForm({
      validationSchema: yup.object({
        name: yup.string().required('Nome é obrigatório'),
        phone: yup.string().required('Telefone é obrigatório'),
        position: yup.string().required('Cargo é obrigatório')
      })
    })

    const nameValue = ref('')
    const positionValue = ref('')
    const phoneValue = ref('')

    const onSubmit = handleSubmit(async () => {
      try {
        await navigator.clipboard.writeText(signaturePreview.value)
        toast('Assinatura de e-mail copiada com sucesso!', {
          theme: 'auto',
          type: 'success',
          dangerouslyHTMLString: true
        })
      } catch (err) {
        toast('Erro ao copiar assinatura. Tente novamente.', {
          theme: 'auto',
          type: 'error'
        })
      }
    })

    const config = useConfig()

    const onSubmitGmail = handleSubmit(async () => {
      const isDark = config.theme === theme.DARK
      if (isDark) {
        toast('Coloque seu admin em modo light para copiar a assinatura para Gmail', {
          theme: 'auto',
          type: 'warning',
          dangerouslyHTMLString: true
        })
        return
      }

      try {
        const tempElement = document.createElement('div')
        tempElement.innerHTML = signaturePreview.value
        document.body.appendChild(tempElement)

        const range = document.createRange()
        range.selectNodeContents(tempElement)

        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(range)

        document.execCommand('copy')

        document.body.removeChild(tempElement)

        toast('Assinatura copiada com sucesso! Cole no Gmail usando Ctrl + V', {
          theme: 'auto',
          type: 'success',
          dangerouslyHTMLString: true
        })
      } catch (err) {
        toast('Erro ao copiar assinatura. Tente novamente.', {
          theme: 'auto',
          type: 'error'
        })
      }
    })

    const close = () => {
      contentActive.value = false
      scroll.value = false
      setTimeout(() => {
        emit('onClose', false)
      }, 300)
    }

    const openModal = () => {
      scroll.value = true
      setTimeout(() => {
        contentActive.value = true
      }, 100)
    }

    onMounted(() => {
      openModal()
    })

    const signaturePreview = computed(() =>
      generateSignatureTemplate({
        name: nameValue.value,
        position: positionValue.value,
        phone: phoneValue.value
      })
    )

    return {
      contentActive,
      nameValue,
      phoneValue,
      positionValue,
      onSubmit,
      onSubmitGmail,
      close,
      signaturePreview
    }
  }
}
</script>

<template>
  <div class="box-content d-flex justify-content-end">
    <div :class="['back', { active: contentActive }]" @click="close()"></div>
    <div :class="['content', { active: contentActive }]">
      <div class="row">
        <h4 class="col-8">{{ title }}</h4>
      </div>

      <div class="signature-preview" v-html="signaturePreview"></div>

      <div class="col-12">
        <div class="row g-2">
          <form-input
            class="col-12 form-input"
            label="Nome"
            v-model="nameValue"
            id="name"
            name="name"
            type="text"
          />
          <form-input
            class="col-12 form-input"
            label="Cargo"
            v-model="positionValue"
            id="position"
            name="position"
            type="text"
          />
          <form-input
            class="col-12 form-input"
            label="Telefone"
            v-model="phoneValue"
            id="phone"
            name="phone"
            type="tel"
          />

          <form-button
            class="col-6 form-input"
            title="Copiar código de assinatura"
            :loading="loading"
            @clicked="onSubmit"
          />

          <form-button
            class="col-6 form-input"
            title="Copiar para Gmail"
            :loading="loading"
            @clicked="onSubmitGmail"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.signature-preview {
  background: #ffffff;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-bottom: 20px;
  p {
    color: #000;
  }
}
</style>
